import { formatTableDurationValue } from './durationFormat.js';
import { buildTimelineFromMembers } from './imageDensityTooltipTimeline.js';
import {
  CUSTOM_HEATMAP_X_COLUMN,
  CUSTOM_HEATMAP_Y_COLUMN,
  formatPositionKeyValue,
} from './imageDensityPoints.js';

const MAX_LIST_ROWS = 30;
const JSON_DISPLAY_MAX = 50000;

export function tryParseJsonValue(value) {
  if (value == null || value === '') return { ok: false };
  if (typeof value === 'object') return { ok: true, parsed: value };
  if (typeof value !== 'string') return { ok: false };
  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return { ok: false };
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object') return { ok: true, parsed };
  } catch {
    /* texto plano */
  }
  return { ok: false };
}

export function tryPrettyJson(value) {
  const { ok, parsed } = tryParseJsonValue(value);
  if (!ok) return null;
  try {
    const json = JSON.stringify(parsed, null, 2);
    if (!json) return null;
    return json.length > JSON_DISPLAY_MAX ? `${json.slice(0, JSON_DISPLAY_MAX - 1)}…` : json;
  } catch {
    return null;
  }
}

function jsonShortLabel(parsed) {
  if (Array.isArray(parsed)) {
    if (parsed[0] && typeof parsed[0] === 'object') return jsonShortLabel(parsed[0]);
    return `${parsed.length} elemento${parsed.length === 1 ? '' : 's'}`;
  }
  if (!parsed || typeof parsed !== 'object') return 'JSON';
  if (typeof parsed.type === 'string' && parsed.type.trim()) return parsed.type.trim();
  if (Array.isArray(parsed.data) && parsed.data[0]) return jsonShortLabel(parsed.data[0]);
  const preferred = ['nombre', 'NOMBRE', 'name', 'title', 'label', 'id', 'ID'];
  for (const k of preferred) {
    if (parsed[k] != null && typeof parsed[k] !== 'object' && String(parsed[k]).trim()) {
      return String(parsed[k]).trim();
    }
  }
  return 'JSON';
}

function findRowKey(row, col) {
  if (!row || !col) return null;
  if (Object.prototype.hasOwnProperty.call(row, col)) return col;
  const lower = String(col).toLowerCase();
  const key = Object.keys(row).find((k) => k.toLowerCase() === lower);
  return key || null;
}

/** Columnas del tooltip que existen en la tabla que alimenta el gráfico. */
export function filterTooltipColumnsForRows(columns, rows) {
  const cols = (columns || []).filter(Boolean);
  if (!cols.length) return [];
  const sample = rows?.[0];
  if (!sample) return cols;
  return cols.filter((c) => findRowKey(sample, c));
}

export function formatCellValue(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (Number.isInteger(value)) return String(value);
    return value.toFixed(4).replace(/\.?0+$/, '');
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
  const pretty = tryPrettyJson(value);
  if (pretty) return pretty;
  if (typeof value === 'object') return formatPositionKeyValue(value) || '—';
  const s = String(value);
  return s.length > 120 ? `${s.slice(0, 117)}…` : s;
}

/** Valor de celda con formato de duración de episodios cuando aplica. */
export function formatDisplayCellValue(value, column, columnDurationFormats) {
  const durationFormatted = formatTableDurationValue(
    value,
    column,
    columnDurationFormats,
    null
  );
  if (durationFormatted != null) return durationFormatted;
  return formatCellValue(value);
}

function buildTableRow(member, index, columns, columnDurationFormats) {
  const row = member?.row || {};
  const cells = columns.map((col) => {
    const key = findRowKey(row, col);
    const raw = key ? row[key] : null;
    const pretty = raw != null && raw !== '' ? tryPrettyJson(raw) : null;
    return {
      column: col,
      value: pretty || (key ? formatDisplayCellValue(raw, col, columnDurationFormats) : '—'),
      isJson: Boolean(pretty),
    };
  });
  return {
    key: `row-${index}`,
    index: index + 1,
    cells,
  };
}

function isNumericLike(value) {
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string' && value.trim() !== '') return Number.isFinite(Number(value));
  return false;
}

function columnLabel(col) {
  return String(col || '').replace(/_/g, ' ');
}

function pointTitle(normalized, members, { positionKeyColumn, groupByColumn }) {
  if (members.length > 1) {
    return `${normalized.count != null ? normalized.count : members.length} elementos agrupados`;
  }
  const member = members[0] || normalized;
  if (member?.label) return String(member.label);
  if (member?.positionKey) return String(member.positionKey);
  const row = member?.row || {};
  if (positionKeyColumn) {
    const key = findRowKey(row, positionKeyColumn);
    if (key && row[key] != null && row[key] !== '') {
      return formatPositionKeyValue(row[key]) || String(row[key]);
    }
  }
  if (groupByColumn) {
    const key = findRowKey(row, groupByColumn);
    if (key && row[key] != null && row[key] !== '') {
      return formatPositionKeyValue(row[key]) || String(row[key]);
    }
  }
  return null;
}

function buildMetrics(members, metricColumns, tooltipColumns, columnDurationFormats) {
  if (!members.length) return [];
  const sampleRow = members[0]?.row || {};
  const preferred = (metricColumns || []).length ? metricColumns : tooltipColumns;
  const cols = filterTooltipColumnsForRows(preferred, [sampleRow]).filter((col) => {
    if (col === CUSTOM_HEATMAP_X_COLUMN || col === CUSTOM_HEATMAP_Y_COLUMN) return false;
    const key = findRowKey(sampleRow, col);
    return key && isNumericLike(sampleRow[key]);
  });
  return cols.map((col) => {
    let total = 0;
    let used = 0;
    for (const member of members) {
      const row = member?.row || {};
      const key = findRowKey(row, col);
      if (!key) continue;
      const n = Number(row[key]);
      if (!Number.isFinite(n)) continue;
      total += n;
      used += 1;
    }
    const value = members.length === 1 || used <= 1 ? (used ? total : null) : total;
    return {
      id: col,
      label: columnLabel(col),
      value:
        value == null
          ? '—'
          : formatDisplayCellValue(value, col, columnDurationFormats),
    };
  });
}

/** Contenido estructurado del tooltip para un punto o cluster. */
export function buildImageDensityTooltipContent(
  point,
  {
    tooltipColumns,
    metricColumns,
    groupByColumn,
    weightColumn,
    positionKeyColumn,
    columnDurationFormats,
  }
) {
  const normalized = point?.members?.length ? point : { ...point, count: 1, members: [point] };
  const members = normalized.members || [];
  const count = normalized.count != null ? normalized.count : members.length;
  const sampleRow = members[0]?.row;
  const cols = sampleRow
    ? filterTooltipColumnsForRows(tooltipColumns, [sampleRow])
    : (tooltipColumns || []).filter(Boolean);
  if (sampleRow) {
    for (const extra of [CUSTOM_HEATMAP_X_COLUMN, CUSTOM_HEATMAP_Y_COLUMN]) {
      if (Object.prototype.hasOwnProperty.call(sampleRow, extra) && !cols.includes(extra)) {
        cols.push(extra);
      }
    }
  }
  const isCluster = count > 1;
  const shownMembers = members.slice(0, MAX_LIST_ROWS);
  const truncatedCount = Math.max(0, members.length - MAX_LIST_ROWS);
  const metrics = buildMetrics(shownMembers, metricColumns, cols, columnDurationFormats);
  const metricIds = new Set(metrics.map((m) => m.id));
  const tableCols = cols.filter((c) => !metricIds.has(c));

  const rawTitle = pointTitle(normalized, shownMembers, { positionKeyColumn, groupByColumn });
  const prettyJson = tryPrettyJson(rawTitle);
  const parsedTitle = prettyJson ? tryParseJsonValue(rawTitle) : { ok: false };
  const title = prettyJson
    ? jsonShortLabel(parsedTitle.parsed)
    : rawTitle;
  const meta = [];
  if (isCluster) {
    if (normalized.group) meta.push(`Serie: ${normalized.group}`);
    if (Number.isFinite(normalized.weight)) {
      meta.push(`Peso fusionado: ${formatCellValue(normalized.weight)}`);
    }
  } else if (members[0]) {
    const p = members[0];
    if (p.group && p.group !== title) meta.push(`Serie: ${p.group}`);
    if (Number.isFinite(p.weight) && p.weight !== 1) meta.push(`Peso: ${formatCellValue(p.weight)}`);
  }

  let columns = tableCols.map((c) => ({ id: c, label: columnLabel(c) }));
  let rows = shownMembers.map((m, i) => buildTableRow(m, i, tableCols, columnDurationFormats));

  if (!columns.length && shownMembers.length === 1) {
    columns = [
      { id: 'x', label: 'x' },
      { id: 'y', label: 'y' },
    ];
    rows = [
      {
        key: 'row-0',
        index: 1,
        cells: [
          { column: 'x', value: formatCellValue(normalized.x) },
          { column: 'y', value: formatCellValue(normalized.y) },
        ],
      },
    ];
  } else if (!columns.length && shownMembers.length > 1) {
    columns = [
      { id: 'coords', label: 'Coordenadas' },
    ];
    if (groupByColumn) {
      columns.unshift({ id: groupByColumn, label: columnLabel(groupByColumn) });
    }
    rows = shownMembers.map((m, i) => {
      const row = m.row || {};
      const cells = [];
      if (groupByColumn) {
        cells.push({
          column: groupByColumn,
          value: row[groupByColumn] != null ? formatCellValue(row[groupByColumn]) : '—',
        });
      }
      const bits = [`(${formatCellValue(m.x)}, ${formatCellValue(m.y)})`];
      if (weightColumn && row[weightColumn] != null) {
        bits.unshift(formatDisplayCellValue(row[weightColumn], weightColumn, columnDurationFormats));
      }
      cells.push({ column: 'coords', value: bits.join(' · ') });
      return { key: `row-${i}`, index: i + 1, cells };
    });
  }

  const timeline = buildTimelineFromMembers(shownMembers);

  return {
    header: {
      title,
      prettyJson,
      meta,
    },
    isCluster,
    count,
    truncatedCount,
    columns,
    rows,
    metrics,
    timeline,
  };
}

/** Hit-test: punto más cercano dentro del radio (coords de canvas). */
export function findNearestImageDensityPoint(canvasX, canvasY, points, hitRadiusPx) {
  if (!points?.length) return null;
  const r = Math.max(4, hitRadiusPx || 14);
  let best = null;
  let bestDist = r;
  for (const p of points) {
    const px = p._cx;
    const py = p._cy;
    if (!Number.isFinite(px) || !Number.isFinite(py)) continue;
    const dx = canvasX - px;
    const dy = canvasY - py;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist <= bestDist) {
      bestDist = dist;
      best = p;
    }
  }
  return best;
}
